import type { NhlTiebreakerTeam } from '../types';
import type { TiebreakerStepFn, StepResult } from '../types';
import { RankMethod } from '../../enums/rank-method';
import { addExplanation } from '../helpers';

/**
 * NHL tiebreaker step 6: Goal Differential (GF - GA).
 *
 * If all teams have zero goalsFor and zero goalsAgainst (season not started /
 * data unavailable), the step is skipped (no-break). Otherwise the team(s)
 * with the highest goal differential win the step.
 */
export function createGoalDifferentialStep(): TiebreakerStepFn<NhlTiebreakerTeam> {
  return (teams, ctx): StepResult<NhlTiebreakerTeam> => {
    const hasScores = teams.some(t => t.goalsFor > 0 || t.goalsAgainst > 0);
    if (!hasScores) {
      for (const t of teams) {
        addExplanation(ctx, t, 'Goal Differential: no score data — skipped.');
      }
      return { winners: [], remaining: teams };
    }

    interface Entry { team: NhlTiebreakerTeam; diff: number }
    const ranked: Entry[] = teams.map(t => ({
      team: t,
      diff: t.goalsFor - t.goalsAgainst,
    }));

    const maxDiff = Math.max(...ranked.map(r => r.diff));
    const winners = ranked.filter(r => r.diff === maxDiff);
    const losers = ranked.filter(r => r.diff < maxDiff);

    if (winners.length === teams.length) {
      for (const r of ranked) {
        addExplanation(ctx, r.team, `Goal Differential: ${r.diff >= 0 ? '+' : ''}${r.diff} — tied.`);
      }
      return { winners: [], remaining: teams };
    }

    for (const r of winners) {
      addExplanation(ctx, r.team, `Goal Differential: ${r.diff >= 0 ? '+' : ''}${r.diff} — best.`);
    }
    for (const r of losers) {
      addExplanation(ctx, r.team, `Goal Differential: ${r.diff >= 0 ? '+' : ''}${r.diff}.`);
    }

    return {
      winners: winners.map(r => r.team),
      remaining: losers.map(r => r.team),
      rankMethod: RankMethod.GoalDifferential,
    };
  };
}

export const goalDifferentialStep = createGoalDifferentialStep();
